import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {vec3} from "#material/types/vector"
import {z} from "zod/v4-mini"
import {materialColorOutput, materialFloatOutput} from "#material/nodes/base/material/validation/io"
import {color} from "@cm/graph-v3"
import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {keepLegacyColorMapping, keepLegacyTextureMapping} from "#material/nodes/texture/legacy-texture-settings"

export interface TexBrickSockets extends MaterialNodeSockets {
    inputs: {
        bias: "float" // found in inputs: -; found in settings: float; inferred from data: float
        brickWidth: "float" // found in inputs: -; found in settings: float; inferred from data: float
        color1: "color" // found in inputs: -; found in settings: color; inferred from data: float3
        color2: "color" // found in inputs: -; found in settings: color; inferred from data: float3
        mortar: "color" // found in inputs: -; found in settings: color; inferred from data: float3
        mortarSize: "float" // found in inputs: -; found in settings: float; inferred from data: float
        mortarSmooth: "float" // found in inputs: -; found in settings: float; inferred from data: float
        rowHeight: "float" // found in inputs: -; found in settings: float; inferred from data: float
        scale: "float" // found in inputs: -; found in settings: float; inferred from data: float
        vector: "point" // found in inputs: color; found in settings: vector; inferred from data: float3
    }
    settings: {
        bias: "float" // found in inputs: -; found in settings: float; inferred from data: float
        brickWidth: "float" // found in inputs: -; found in settings: float; inferred from data: float
        color1: "color" // found in inputs: -; found in settings: color; inferred from data: float3
        color2: "color" // found in inputs: -; found in settings: color; inferred from data: float3
        fac: "float" // found in inputs: -; found in settings: float; inferred from data: float
        mortar: "color" // found in inputs: -; found in settings: color; inferred from data: float3
        mortarSize: "float" // found in inputs: -; found in settings: float; inferred from data: float
        mortarSmooth: "float" // found in inputs: -; found in settings: float; inferred from data: float
        offset: "float" // found in inputs: -; found in settings: float; inferred from data: float
        offsetFrequency: "int" // found in inputs: -; found in settings: int; inferred from data: float
        rowHeight: "float" // found in inputs: -; found in settings: float; inferred from data: float
        scale: "float" // found in inputs: -; found in settings: float; inferred from data: float
        squash: "float" // found in inputs: -; found in settings: float; inferred from data: float
        squashFrequency: "int" // found in inputs: -; found in settings: int; inferred from data: float
        vector: "point" // found in inputs: color; found in settings: vector; inferred from data: float3
    }
    outputs: {
        color: "color"
        fac: "float"
    }
}

// there is nothing to smooth where the bricks meet with no mortar between them
const HAS_MORTAR = {setting: "mortarSize", whenSet: true} as const

export const texBrickDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeTexBrick,
    },
    nodeClass: "TexBrick",
    ui: {
        label: "Brick Texture",
        color: "#4987af",
        items: [
            {name: "color", valueType: "color", label: "Color", output: true},
            {name: "fac", valueType: "float", label: "Fac", output: true},
            {
                name: "vector",
                valueType: "point",
                label: "Vector",
                input: true,
                setting: true,
                defaultValue: {x: 0, y: 0, z: 0},
            },
            {name: "scale", valueType: "float", label: "Scale", input: true, setting: true, defaultValue: 5},
            {name: "mortarSize", valueType: "float", label: "Mortar Size", input: true, range: {min: 0, max: 0.125}, setting: true, defaultValue: 0.02},
            {
                name: "mortarSmooth",
                valueType: "float",
                label: "Mortar Smooth",
                input: true,
                range: {min: 0, max: 1},
                setting: true,
                defaultValue: 0,
                visibleWhen: HAS_MORTAR,
            },
            {name: "bias", valueType: "float", label: "Bias", input: true, range: {min: -1, max: 1}, setting: true, defaultValue: 0},
            {name: "brickWidth", valueType: "float", label: "Brick Width", input: true, setting: true, defaultValue: 0.5},
            {name: "rowHeight", valueType: "float", label: "Row Height", input: true, setting: true, defaultValue: 0.25},
            {name: "offset", valueType: "float", label: "Offset", input: false, range: {min: 0, max: 1}, setting: true, defaultValue: 0.5},
            {name: "offsetFrequency", valueType: "int", label: "Offset Frequency", input: false, setting: true, defaultValue: 2},
            {name: "squash", valueType: "float", label: "Squash", input: false, setting: true, defaultValue: 1},
            {name: "squashFrequency", valueType: "int", label: "Squash Frequency", input: false, setting: true, defaultValue: 2},
            {
                name: "color1",
                valueType: "color",
                label: "Color1",
                input: true,
                setting: true,
                defaultValue: {r: 0.0, g: 0.0, b: 0.0},
            },
            {
                name: "color2",
                valueType: "color",
                label: "Color2",
                input: true,
                setting: true,
                defaultValue: {r: 0.0, g: 0.0, b: 0.0},
            },
            {
                name: "mortar",
                valueType: "color",
                label: "Mortar",
                input: true,
                setting: true,
                defaultValue: {r: 0.0, g: 0.0, b: 0.0},
            },
        ],
        support: {
            cycles: true,
            three: false,
        },
    },
    validation: {
        returns: z.strictObject({color: materialColorOutput, fac: materialFloatOutput}),
        parameters: z.strictObject({
            inputs: z.strictObject({
                bias: optionalMaterialInput,
                brickWidth: optionalMaterialInput,
                color1: optionalMaterialInput,
                color2: optionalMaterialInput,
                mortar: optionalMaterialInput,
                mortarSize: optionalMaterialInput,
                mortarSmooth: optionalMaterialInput,
                rowHeight: optionalMaterialInput,
                scale: optionalMaterialInput,
                vector: optionalMaterialInput,
            }),
            settings: z.strictObject({
                bias: z.optional(z.number()),
                brickWidth: z.optional(z.number()),
                color1: z.optional(color),
                color2: z.optional(color),
                color: z.optional(color),
                ...keepLegacyColorMapping,
                fac: z.optional(z.number()),
                mortar: z.optional(color),
                mortarSize: z.optional(z.number()),
                mortarSmooth: z.optional(z.number()),
                offset: z.optional(z.number()),
                offsetFrequency: z.optional(z.int()),
                rowHeight: z.optional(z.number()),
                scale: z.optional(z.number()),
                squash: z.optional(z.number()),
                squashFrequency: z.optional(z.int()),
                ...keepLegacyTextureMapping,
                vector: z.optional(vec3),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<TexBrickSockets>
